// Bolo gameplay constants. Sources cited in comments:
//   [Cheshire] = Stuart Cheshire's Brain.h / Maps&BrainsFAQ
//   [WinBolo]  = WinBolo source (kippandrew/winbolo) at references/winbolo/

// --- World / time ---
export const TICK_HZ = 50; // [Cheshire] Maps&BrainsFAQ: "1/50th second"
export const TICK_MS = 20;
export const WORLD_UNITS_PER_TILE = 256; // [Cheshire] How to write plug-in brains
export const MAP_SIZE = 256;
export const MAP_USABLE_BORDER = 20; // tiles of forced deepsea margin
export const MINE_EDGE_LEFT = 20; // [WinBolo] bolo_map.h:39-42
export const MINE_EDGE_RIGHT = 236;
// File-format limits from Cheshire's BMAP spec; the ~3,500-map corpus depends
// on these. NOT the player cap — see MAX_PLAYERS below.
export const MAX_PILLS = 16;
export const MAX_BASES = 16;
export const MAX_STARTS = 16;

// --- Players ---
// Runtime cap on concurrent players in a Room. Keep ≤ MAX_STARTS so every
// player gets a unique BMAP start tile when the map provides enough. 16
// matches Cheshire's original cap and his Brain.h's PlayerBitMap (u_long
// — 32-bit, deliberately sized for ≥16 players).
export const MAX_PLAYERS = 16;

// --- Tank ---
export const TANK_FULL_ARMOUR = 40; // [WinBolo] gametype.h:38
export const TANK_FULL_SHELLS = 40;
export const TANK_FULL_MINES = 40;
export const TANK_FULL_TREES = 40;
export const TANK_RELOAD_TICKS = 13; // [WinBolo] tank.h:123 — 260ms
export const TANK_DEATH_WAIT_TICKS = 255;
export const TANK_ACCEL_RATE = 0.25; // world-units/tick² [WinBolo] tank.h:96
export const TANK_DECEL_RATE = 0.25;
// Tick budget for water drain: every TANK_WATER_TICKS while a tank is in
// shallow water (River) and not on a boat, it loses 1 shell and 1 mine.
// [WinBolo] tank.h TANK_WATER_TIME, tank.c tankInWater()
export const TANK_WATER_TICKS = 15;
// Ticks of held turn input before the full per-terrain rate kicks in.
export const TANK_TURN_RAMPUP_TICKS = 10;
// Knockback when a tank is hit by a shell. The tank slides TANK_SLIDE_DIST
// world units along the shell's direction per tick for TANK_SLIDE_TICKS
// (collision-checked each step). [WinBolo] tank.h:55-57, tank.c:946-949 +
// 1554-1556.
export const TANK_SLIDE_DIST = 16;
export const TANK_SLIDE_TICKS = 7;
// (No TANK_BUMP_DIST / TANK_BUMP_TICKS constants — wall bumps don't arm a
// slide. The "obstructed" cue surfaced to the brain comes from
// `tank.obstructed`, set in movement.ts when an axis check fails.
// Knockback uses TANK_SLIDE_DIST / TANK_SLIDE_TICKS above.)
// [WinBolo] tank.h:139 TANK_WALL_SLOW_DOWN — speed penalty when BOTH X and
// Y movement axes are blocked (tank is driving straight into a wall, not
// brushing past). One-axis blocks (sliding along the wall) keep speed.
export const TANK_WALL_SLOW_DOWN = 1;

// --- Gunsight (range adjustment) ---
// Gunsight range in half-tiles. Player tweaks via TankKey.MoreRange / LessRange
// to set how far the next shell flies (TTL is computed from sightLen so the
// shell despawns at the desired range). [Cheshire] Brain.h `MAXRANGE 14
// // In HALF map-squares` — canonical Mac Bolo cap; WinBolo carries the same
// value at tank.h:120. With sightLen=14 the shell flies 14 × 4 ticks ×
// SHELL_SPEED = 1792 wu = 7 tiles from the muzzle. Combined with the
// half-tile muzzle offset on both sides, this exactly matches pillbox
// detection range (8 tiles centre-to-centre) — i.e. tank max range and pill
// detection meet head-on with no free-shot zone, which is Cheshire's design.
export const GUNSIGHT_MIN = 2;
export const GUNSIGHT_MAX = 14;
export const GUNSIGHT_DEFAULT = GUNSIGHT_MAX;
// Ticks between consecutive sightLen ±1 steps while the key is held. WinBolo
// adjusts every tick (~240 ms full traversal at 50 Hz); we slow it to ~1 s so
// the player can land on a precise value without overshooting.
export const GUNSIGHT_STEP_TICKS = 4;

// --- Shell ---
export const SHELL_SPEED = 32; // world-units/tick [WinBolo] shells.h:52
// Max shell flight in tiles, derived from GUNSIGHT_MAX (= 14 half-tiles
// = 7 tiles). Kept as a documentation constant; runtime shell TTL is
// computed from each tank's current sightLen — see shellTtlForSightLen.
export const SHELL_LIFE_TILES = 7;
export const SHELL_DAMAGE = 5; // [WinBolo] global.h:236 — generic per-hit damage
// Grace period after firing during which the shell can't hit its own tank
// (otherwise the muzzle-front spawn would self-collide on the next tick).
export const SHELL_SELF_HIT_GRACE_TICKS = 2;

// --- Mine ---
export const MINE_DAMAGE = 10; // [WinBolo] tank.h:132
export const MINE_CHAIN_WAIT_TICKS = 10; // [WinBolo] minesexp.h:42

// --- Pillbox ---
// All pillbox constants verified against Cheshire's Maps&BrainsFAQ ("How is
// the firing for pillboxes controlled?"): "A pillbox fires once every x
// ticks. For a quiescent pillbox x is 100 (two seconds). Each time it is hit,
// x halves, down to a minimum of 4 ticks (12.5 shots per second). x 'relaxes'
// back to the quiescent state at a rate of one unit every 32 ticks."
export const PILL_MAX_HEALTH = 15; // [Cheshire] Brain.h MAX_PILL_ARMOUR
export const PILL_RANGE_WORLD = 2048; // 8 tiles, [Cheshire] Maps&BrainsFAQ
export const PILL_FIRE_QUIESCENT = 100; // [Cheshire] Maps&BrainsFAQ
export const PILL_FIRE_MIN = 4; // [Cheshire] Maps&BrainsFAQ — floor at 4
export const PILL_COOLDOWN_DECAY_TICKS = 32; // [Cheshire] Maps&BrainsFAQ
export const PILL_REPAIR_PER_TREE = 4;
// Maximum shell flight distance from a pillbox shot — 9 map squares.
// [Cheshire] Maps&BrainsFAQ: "Pillbox shots go nine map squares (2304 World units)."
export const PILL_SHOT_RANGE_WORLD = 2304;
// When a friendly base takes a shell hit, allied pillboxes within ±this many
// tiles get angry: speed halved (floored at PILL_FIRE_MIN) and cooldown
// reset. [WinBolo] pillbox.h:57-60 PILL_BASE_HIT_LEFT/RIGHT/TOP/BOTTOM = ±9.
export const PILL_BASE_HIT_RANGE = 9;

// --- Refuel base ---
// Stock caps + capture thresholds from [Cheshire] Brain.h.
export const BASE_FULL_ARMOUR = 90; // MAX_BASE_ARMOUR
export const BASE_FULL_SHELLS = 90; // MAX_BASE_SHELLS
export const BASE_FULL_MINES = 90; // MAX_BASE_MINES
export const BASE_DEAD = 9;
export const BASE_MIN_ARMOUR_CAPTURE = 9;
// A shell fired at a base only damages + stops on it when the base's
// armour is greater than this threshold. Below it (e.g. a near-dead
// base regenerating from 0), the shell flies through unobstructed.
// [WinBolo] bases.h:51 BASE_MIN_CAN_HIT — 4.
export const BASE_MIN_CAN_HIT = 4;
// HUD-only: the base-status panel becomes visible when the local tank is
// within this many world units of a friendly/neutral base. 1792 = 7 tiles.
// [WinBolo] bases.h:76.
export const BASE_STATUS_RANGE = 1792;
// Refuel cadence per [Cheshire] Maps&BrainsFAQ: "Armour is repaired at the
// rate of at most one unit per second. Shells and mines refuel at a rate of
// at most ten per second." At 50 Hz that's: armour every 50 ticks, shells +
// mines every 5 ticks. Each pulse delivers 1 unit of the corresponding
// resource (and decrements the base's stock by 1).
export const BASE_REFUEL_ARMOUR_INTERVAL_TICKS = 50;
export const BASE_REFUEL_SHELLS_INTERVAL_TICKS = 5;
export const BASE_REFUEL_MINES_INTERVAL_TICKS = 5;
// Stock regeneration: per [Cheshire] Maps&BrainsFAQ "Every twenty seconds
// refuelling bases replenish their stocks by one unit per player". 20 s @
// 50 Hz = 1000 ticks per regen cycle, then yield = playerCount units.
export const BASE_REGEN_INTERVAL_TICKS = 1000;
// Damage absorbed per shell hit (and ARMOUR_COST per base armour unit).
// [Cheshire] Brain.h ARMOUR_COST = BASE_RESIST_SHELLS.
export const ARMOUR_COST = 5;
export const BASE_RESIST_SHELLS = 5;
export const BASE_RESIST_TANKS = 10; // [Cheshire] Brain.h ARMOUR_COST*2

// --- LGM (Little Green Man) ---
export const LGM_BUILD_TICKS = 20; // [WinBolo] lgm.h:77 — uniform per action
export const LGM_HELICOPTER_SPEED = 3;
export const LGM_FARM_YIELD = 4; // tree units per chop

export const LGMAction = {
  Farm: 0,
  Road: 1,
  Building: 2,
  Pillbox: 3,
  Mine: 4,
  Boat: 5,
} as const;

export type LGMActionCode = (typeof LGMAction)[keyof typeof LGMAction];

// Tree costs per action [WinBolo] lgm.h:49-55
export const TREE_COST_ROAD = 2;
export const TREE_COST_BUILDING = 2;
export const TREE_COST_REPAIR_BUILDING = 1;
export const TREE_COST_BOAT = 20;
export const TREE_COST_NEW_PILL = 4;
export const TREE_COST_REPAIR_PILL = 1;
export const TREE_COST_MINE = 1;

// Max pillboxes a single tank can carry at once. WinBolo: MAX_PILLS (16).
export const TANK_MAX_CARRIED_PILLS = 16;

// --- Boat ---
// [Cheshire] Maps&BrainsFAQ ("What are the various maximum speeds of things
// in Bolo?"): "The top speed of a tank on a boat is 16 World units per tick."
// WinBolo agrees (MAP_SPEED_TBOAT = 16 in bolo_map.h:84, applied as the speed
// cap for tanks in DeepSea/River with onBoat == TRUE).
export const BOAT_MAX_SPEED = 16;
export const BOAT_DEFAULT_DRIFT = 0; // boat starts stationary; player throttles up
export const BOAT_ACCEL_RATE = 0.25; // matches tank accel
export const BOAT_TURN_RATE = 0.75; // bradians/tick — between road (1.0) and rough (0.5)
// (No BOAT_FULL_ARMOUR — boats don't have HP. Shells go through to the
// rider via applyShellHitTank, which ejects them and drops the boat in
// one hit. Mirrors WinBolo tank.c:911-924.)
// (No BOAT_EXIT_SPEED_CAP — boat→tank speed handoff is uncapped. WinBolo's
// `tankMoveOnBoat` (tank.c:1364) just flips onBoat = FALSE without touching
// speed; per-terrain decel slows the tank naturally once it's on land.
// Cheshire's docs don't define disembark semantics; WinBolo is the only
// precedent.)

// --- Terrain degradation ---
// Generic LIFE counter for shellable terrains (halfbuilding, grass, rubble,
// swamp). WinBolo had a per-tile LIFE; we use one shared value for simplicity.
export const TERRAIN_HIT_LIFE = 4;

// --- Tank death explosion ---
// "Big" explosion threshold: if a tank's shells + mines exceed this at death,
// the explosion blasts a 3×3 patch of terrain around the tank's tile.
// [WinBolo] tank.h:129 TANK_BIG_EXPLOSION_THRESHOLD = 20.
export const TANK_BIG_EXPLOSION_THRESHOLD = 20;
// Damage applied to each pillbox in the blast radius. [WinBolo] tankexp.h:47
export const TANK_EXPLOSION_PILL_DAMAGE = 5;
// Visual lifetime of the explosion entity (ticks). [WinBolo] tankexp.h:44
export const TANK_EXPLOSION_TTL_TICKS = 40;

// --- Forest ---
export const FOREST_REGROW_TICKS = 3000; // 60s [WinBolo] treegrow.h:35
export const FOREST_REGROW_INITIAL = 30000;
// Tanks in forest are hidden from observers (pillboxes, other players)
// further than this many world-units away. [WinBolo] tank.h:149
export const FOREST_HIDE_DIST = 768; // 3 tiles

// --- Flood ---
export const FLOOD_FILL_WAIT_TICKS = 16; // [WinBolo] floodfill.h:41

// --- Game modes ---
export const GameType = {
  Open: 1,
  Tournament: 2,
  StrictTournament: 3,
} as const;

export type GameTypeCode = (typeof GameType)[keyof typeof GameType];
